import React, { useCallback, useMemo, useState } from 'react';
import { Badge, Box, Stack, Typography } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import CardView from '../Redesing/card-view';
import { PaginationTable } from '../Redesing/table/pagination-table';
import MovaticCustomModal from '../Modal/MovaticCustomModal';
import RentalImageGallery from '../RentalImageGallery/RentalImageGallery';
import { getLocaleDateTimeString, history } from '../../utils';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import type { IColumn } from '../../ts/interface/components/redesing/table.interface';
import type { RentalGraphQL } from '../../hooks/hardware/query/V2/useHardwareRentalsRelay';

interface MechanicRentalsListProps {
  rentals: RentalGraphQL[];
  isLoading: boolean;
  ticketResolved: boolean;
}

interface GalleryImage {
  link: string;
  category?: string;
  note?: string | null;
}

const formatDuration = (seconds?: number | null): string => {
  if (seconds == null || Number.isNaN(seconds) || seconds < 0) return '—';
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
};

const formatCharge = (amount?: number | null): string => {
  if (amount == null || Number.isNaN(amount)) return '—';
  return `$${amount.toFixed(2)}`;
};

const userDisplayName = (rental: RentalGraphQL): string => {
  const u = rental.user;
  if (!u) return '—';
  if (u.fullName?.trim()) return u.fullName;
  const composed = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  return composed || '—';
};

// rental.images items can be either JSON strings or already-parsed objects
// shaped like {link, category, note}; sometimes the parsed payload itself is an
// array of those objects. rental.endImageUrl is a plain URL. We mirror the
// merging that rentalDetail.tsx does and accept all of those shapes so no photo
// is dropped.
const pushGalleryImage = (out: GalleryImage[], candidate: any) => {
  if (!candidate) return;
  if (Array.isArray(candidate)) {
    for (const item of candidate) pushGalleryImage(out, item);
    return;
  }
  if (typeof candidate === 'string') {
    if (candidate.startsWith('http')) out.push({ link: candidate });
    return;
  }
  if (typeof candidate.link === 'string') {
    out.push({
      link: candidate.link,
      category: candidate.category,
      note: candidate.note ?? null,
    });
  }
};

const getRentalGalleryImages = (rental: RentalGraphQL): GalleryImage[] => {
  const out: GalleryImage[] = [];
  if (rental.endImageUrl) {
    out.push({ link: rental.endImageUrl, category: 'End Rental Photo', note: null });
  }
  const rawImages = (rental.images ?? []) as readonly (string | object)[];
  for (const raw of rawImages) {
    if (typeof raw === 'string') {
      try {
        pushGalleryImage(out, JSON.parse(raw));
      } catch {
        pushGalleryImage(out, raw);
      }
    } else {
      pushGalleryImage(out, raw);
    }
  }
  return out;
};

const MechanicRentalsList: React.FC<MechanicRentalsListProps> = ({
  rentals,
  isLoading,
  ticketResolved,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [galleryRental, setGalleryRental] = useState<RentalGraphQL | null>(null);

  const closeGallery = useCallback(() => setGalleryRental(null), []);
  const openGallery = useCallback((rental: RentalGraphQL) => {
    setGalleryRental(rental);
  }, []);

  const title = ticketResolved
    ? 'Mechanic Rentals (24h before resolution)'
    : 'Mechanic Rentals (last 24h)';

  const items = useMemo(() => rentals, [rentals]);

  const galleryImages = useMemo(
    () => (galleryRental ? getRentalGalleryImages(galleryRental) : []),
    [galleryRental]
  );

  const galleryTitle = useMemo(() => {
    if (!galleryRental) return 'Rental Photos';
    const userName = userDisplayName(galleryRental);
    const time = galleryRental.startTime
      ? getLocaleDateTimeString(galleryRental.startTime)
      : '';
    return `Photos · ${userName}${time ? ` · ${time}` : ''}`;
  }, [galleryRental]);

  const columns: IColumn[] = useMemo(
    () => [
      {
        id: 'startTime',
        label: 'Start Time',
        format: (row: RentalGraphQL) =>
          row.startTime ? getLocaleDateTimeString(row.startTime) : '—',
      },
      {
        id: 'user',
        label: 'User',
        notSortable: true,
        format: (row: RentalGraphQL) => userDisplayName(row),
      },
      {
        id: 'duration',
        label: 'Duration',
        notSortable: true,
        format: (row: RentalGraphQL) => formatDuration(row.duration),
      },
      {
        id: 'chargeAmount',
        label: 'Charge',
        notSortable: true,
        format: (row: RentalGraphQL) => formatCharge(row.chargeAmount),
      },
      {
        id: 'photos',
        label: 'Photos',
        align: 'center',
        notSortable: true,
        disableCLick: true,
        format: (row: RentalGraphQL) => {
          const images = getRentalGalleryImages(row);
          if (images.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }
          const firstUrl = images[0].link;
          return (
            <Box sx={{ display: 'inline-flex' }}>
              <Badge
                badgeContent={images.length}
                color="primary"
                overlap="rectangular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.65rem',
                    height: 18,
                    minWidth: 18,
                    padding: '0 4px',
                  },
                }}
              >
                <Box
                  component="img"
                  src={firstUrl}
                  alt="Rental photo preview"
                  loading="lazy"
                  onClick={(e) => {
                    e.stopPropagation();
                    openGallery(row);
                  }}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    objectFit: 'cover',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 2,
                    },
                  }}
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.onerror = null;
                    target.style.display = 'none';
                  }}
                />
              </Badge>
            </Box>
          );
        },
      },
    ],
    [openGallery]
  );

  const emptyStateNode = (
    <Stack spacing={1} alignItems="center" textAlign="left">
      <Typography variant="body2" color="text.secondary">
        No rentals from mechanic-membership users were found in the last 24 hours for
        this hardware.
      </Typography>
    </Stack>
  );

  return (
    <>
      <CardView
        title={title}
        content={
          <PaginationTable
            isLoading={isLoading}
            customKey={'id'}
            columns={columns}
            items={items}
            hideSearch
            showFilter={false}
            noDataText={emptyStateNode}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
            onRowCLick={(row: RentalGraphQL) => {
              history.push(`/rentals/${row.id}`);
            }}
            onClickReturnRow
          />
        }
      />
      <MovaticCustomModal
        open={Boolean(galleryRental)}
        onClose={closeGallery}
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <ImageIcon fontSize="small" />
            <Typography variant="h6" component="span">
              {galleryTitle}
            </Typography>
          </Stack>
        }
        customStyles={{ content: { minWidth: '40rem', maxWidth: '90vw' } }}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: '900px',
            width: '100%',
          },
        }}
        content={<RentalImageGallery images={galleryImages} title="Rental Images" />}
      />
    </>
  );
};

export default MechanicRentalsList;
